import React, { useState } from "react";
import PropTypes from "prop-types";
import { Modal } from "react-bootstrap";

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.5;

const clampZoom = (value) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));

export default function PreviewableImage({
  src,
  alt,
  imageClassName,
  imageStyle,
  previewTitle,
  buttonClassName,
  hintText,
  resetText,
  zoomLabel,
}) {
  const [showPreview, setShowPreview] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(MIN_ZOOM);

  const onOpenPreview = () => {
    setShowPreview(true);
    setZoomLevel(MIN_ZOOM);
  };

  const onClosePreview = () => {
    setShowPreview(false);
    setZoomLevel(MIN_ZOOM);
  };

  const onZoomIn = () => {
    setZoomLevel((currentZoom) => clampZoom(currentZoom + ZOOM_STEP));
  };

  const onZoomOut = () => {
    setZoomLevel((currentZoom) => clampZoom(currentZoom - ZOOM_STEP));
  };

  const onResetZoom = () => {
    setZoomLevel(MIN_ZOOM);
  };

  const onToggleZoom = () => {
    setZoomLevel((currentZoom) => (currentZoom === MIN_ZOOM ? 2 : MIN_ZOOM));
  };

  return (
    <>
      <button
        type="button"
        className={`previewable-image ${buttonClassName || ""}`}
        onClick={onOpenPreview}
      >
        <img
          className={imageClassName}
          src={src}
          alt={alt}
          style={imageStyle}
        />
        <span className="previewable-image__hint">{hintText}</span>
      </button>

      <Modal
        centered
        dialogClassName="previewable-image-modal"
        show={showPreview}
        onHide={onClosePreview}
      >
        <Modal.Header closeButton>
          <Modal.Title>{previewTitle || alt}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="previewable-image-modal__body">
          <div className="previewable-image-modal__viewport">
            <img
              className="previewable-image-modal__image"
              src={src}
              alt={alt}
              style={{ width: `${zoomLevel * 100}%` }}
              onClick={onToggleZoom}
            />
          </div>
        </Modal.Body>
        <Modal.Footer className="previewable-image-modal__footer">
          <div className="previewable-image-modal__zoom-indicator">
            {zoomLabel} {Math.round(zoomLevel * 100)}%
          </div>
          <div className="previewable-image-modal__actions">
            <button
              type="button"
              className="previewable-image-modal__control"
              onClick={onZoomOut}
              disabled={zoomLevel <= MIN_ZOOM}
            >
              -
            </button>
            <button
              type="button"
              className="previewable-image-modal__control previewable-image-modal__control--reset"
              onClick={onResetZoom}
            >
              {resetText}
            </button>
            <button
              type="button"
              className="previewable-image-modal__control"
              onClick={onZoomIn}
              disabled={zoomLevel >= MAX_ZOOM}
            >
              +
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
}

PreviewableImage.propTypes = {
  alt: PropTypes.string.isRequired,
  buttonClassName: PropTypes.string,
  hintText: PropTypes.string,
  imageClassName: PropTypes.string,
  imageStyle: PropTypes.object,
  previewTitle: PropTypes.string,
  resetText: PropTypes.string,
  src: PropTypes.string.isRequired,
  zoomLabel: PropTypes.string,
};

PreviewableImage.defaultProps = {
  buttonClassName: "",
  hintText: "Preview",
  imageClassName: "",
  imageStyle: {},
  previewTitle: "",
  resetText: "Reset",
  zoomLabel: "Zoom",
};
